<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import MarkdownView from '@/components/MarkdownView.vue';
import { useApiError } from '@/composables/useApiError';
import { useAuthStore } from '@/stores/auth';

/** Блокирующий экран: пока новые версии документов не приняты, работать нельзя. «Отказаться» = выйти. */
const auth = useAuthStore();
const router = useRouter();
const { t, locale } = useI18n();
const { messageFor } = useApiError();

const checked = ref<Record<string, boolean>>({});
const opened = ref<Record<string, boolean>>({});
const submitting = ref(false);
const error = ref<string | null>(null);

const allChecked = computed(() => auth.pendingLegal.every((d) => checked.value[d.id]));

async function accept() {
  if (!allChecked.value || submitting.value) return;
  submitting.value = true;
  error.value = null;
  try {
    await auth.acceptLegal(auth.pendingLegal.map((d) => d.id), locale.value);
  } catch (e) {
    error.value = messageFor(e);
  } finally {
    submitting.value = false;
  }
}

async function decline() {
  await auth.logout();
  await router.replace({ name: 'login' });
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(locale.value, { dateStyle: 'long' });
}
</script>

<template>
  <div class="fixed inset-0 z-50 overflow-y-auto bg-surface/95 px-4 py-8" role="dialog" aria-modal="true">
    <div class="mx-auto max-w-2xl space-y-4">
      <div class="card space-y-2">
        <h2 class="text-xl font-bold">{{ t('legal.gateTitle') }}</h2>
        <p class="text-sm text-muted">{{ t('legal.gateHint') }}</p>
      </div>

      <div v-for="doc in auth.pendingLegal" :key="doc.id" class="card space-y-3">
        <div class="flex flex-wrap items-baseline justify-between gap-2">
          <h3 class="text-lg font-semibold">{{ doc.title }}</h3>
          <span class="text-xs text-muted">{{ t('legal.version', { n: doc.version, date: formatDate(doc.publishedAt) }) }}</span>
        </div>
        <button type="button" class="text-sm text-brand-600 underline" @click="opened[doc.id] = !opened[doc.id]">
          {{ opened[doc.id] ? t('legal.hideText') : t('legal.showText') }}
        </button>
        <MarkdownView v-if="opened[doc.id]" :source="doc.body" class="max-h-96 overflow-y-auto rounded-xl border border-line p-4" />
        <label class="flex items-start gap-3 text-sm">
          <input v-model="checked[doc.id]" type="checkbox" class="mt-1 h-5 w-5" />
          <span>{{ t('legal.accept', { title: doc.title }) }}</span>
        </label>
      </div>

      <p v-if="error" class="text-sm text-danger" role="alert">{{ error }}</p>

      <div class="flex flex-col gap-2 sm:flex-row">
        <button type="button" class="btn-primary flex-1" :disabled="!allChecked || submitting" @click="accept">
          {{ t('legal.acceptAll') }}
        </button>
        <button type="button" class="btn-ghost flex-1" @click="decline">{{ t('legal.decline') }}</button>
      </div>
    </div>
  </div>
</template>
